import { Box, CircularProgress, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { MOTION } from "../../../theme/tokens";

type Props = {
	/** When true, the wrapped content is dimmed + frozen and a centered spinner is overlaid. */
	busy: boolean;
	children: ReactNode;
	/** Spinner diameter in px. Defaults to 32 (the app's in-place "recalculating" size). */
	spinnerSize?: number;
	/** Layered onto the positioning wrapper (e.g. to add margins). */
	sx?: SxProps<Theme>;
	/**
	 * Makes the wrapper height-transparent (flex column, `height: 100%`) so a child that fills its
	 * container — e.g. a full-height grid that owns its own scroll — still resolves its height.
	 */
	fillHeight?: boolean;
	/** Test id hung on the overlay spinner. */
	dataTestId?: string;
};

/**
 * Wraps a surface that recomputes in place: while `busy`, the children are dimmed
 * (`opacity: 0.5`) and made non-interactive so the user keeps spatial context instead of a
 * layout swap, and a centered spinner fades in on top. The dimmed wrapper is marked `inert`
 * (not just `pointerEvents: none`) so it's also removed from the tab order + a11y tree while
 * busy — a keyboard user can't focus frozen controls. The spinner stays mounted and only
 * fades its opacity, so it doesn't pop in against the fading content. Reduced-motion drops
 * both fades. Generic — used by any tab/panel that reruns an expensive calculation (PBJ
 * Prediction / Audit / Submission recalc, etc.).
 */
export const BusyOverlay: FC<Props> = ({ busy, children, spinnerSize = 32, sx, fillHeight = false, dataTestId }) => (
	<Box sx={[{ position: "relative" }, fillHeight && { display: "flex", flexDirection: "column", minHeight: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
		<Box
			// `inert` (React 19 boolean attr) fully disables the subtree — no pointer events AND no
			// focus/tab-stops — while busy; `undefined` (not `false`) leaves the attribute off when idle.
			inert={busy || undefined}
			sx={{
				opacity: busy ? 0.5 : 1,
				transition: `opacity ${MOTION.DURATION.OVERLAY_FADE}ms`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				...(fillHeight ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } : {}),
			}}
			aria-busy={busy}
		>
			{children}
		</Box>
		<Box
			sx={{
				position: "absolute",
				top: 0,
				left: 0,
				right: 0,
				display: "flex",
				justifyContent: "center",
				pt: 4,
				pointerEvents: "none",
				opacity: busy ? 1 : 0,
				transition: `opacity ${MOTION.DURATION.OVERLAY_FADE}ms`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			}}
			aria-hidden={!busy}
		>
			<CircularProgress size={spinnerSize} data-testid={dataTestId} />
		</Box>
	</Box>
);
